// packages/shared-schema/src/alarm.ts
//
// Per `alarm-vtuber-api` — shared contract for the external AI Alarm
// VTuber app: the 6 mood keys, the default prompt template, the wake
// request body, and the prompt composer that fuses the operator template
// with the alarm purpose + fan↔character affinity/memory.
import { z } from 'zod';
/** The 6 mood keys the app's `MoodKey` enum hardcodes — not more, not
 *  fewer. A missing key → the app falls back to the thumbnail. */
export const ALARM_MOOD_KEYS = [
    'morning',
    'afternoon',
    'evening',
    'late_night',
    'alarm_just_set',
    'birthday',
];
export const AlarmTimeSlotEnum = z.enum(['morning', 'afternoon', 'evening', 'late_night']);
export const AlarmWeekdayEnum = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
/** Default operator prompt template (繁中). The composer fills the `{var}`
 *  slots; unknown tokens are left verbatim. The clause vars already carry
 *  their own leading punctuation. */
export const DEFAULT_ALARM_PROMPT_TEMPLATE = '你扮演 {character_name}，一位{character_description}。\n\n' +
    '對你來說，{nickname} 是一個對你而言特別的粉絲{relationship_clause}。\n\n' +
    '{nickname} 設了 {alarm_time} 的鬧鐘，在 {time_slot}（{weekday_zh}、{is_weekday}）起床' +
    '{birthday_clause}{alarm_label_clause}。這次鬧鐘的目的是：{purpose}。\n\n' +
    '請用 {character_name} 的口吻叫 {nickname} 起床，並呼應上面的目的與你們的關係。' +
    '用 50–80 字、繁體中文、口語、不要任何 emoji 或括弧表情。\n\n直接輸出台詞：';
const WEEKDAY_ZH = {
    mon: '週一', tue: '週二', wed: '週三', thu: '週四', fri: '週五', sat: '週六', sun: '週日',
};
/** Wake request body (POST /api/alarm/characters/{id}/wake). */
export const AlarmWakeBodySchema = z.object({
    purpose: z.string().min(1).max(200),
    alarmLabel: z.string().max(60).optional(),
    localTime: z.string().regex(/^\d{2}:\d{2}$/),
    weekday: AlarmWeekdayEnum,
    timeSlot: AlarmTimeSlotEnum,
    nickname: z.string().max(40).optional(),
    isUserBirthday: z.boolean().optional(),
});
/**
 * Fill the operator's template (or the default) with the runtime vars.
 * Clause vars (`{relationship_clause}` / `{birthday_clause}` /
 * `{alarm_label_clause}`) include their own leading comma; empty when the
 * data is absent. Unknown `{tokens}` are left untouched.
 */
export function buildAlarmPrompt(template, v) {
    const tpl = template && template.trim().length > 0 ? template : DEFAULT_ALARM_PROMPT_TEMPLATE;
    const nickname = v.nickname && v.nickname.trim() ? v.nickname.trim() : '你';
    const slot = {
        morning: '早上', afternoon: '下午', evening: '傍晚', late_night: '深夜',
    };
    const weekend = v.weekday === 'sat' || v.weekday === 'sun';
    const map = {
        '{character_name}': v.characterName,
        '{character_description}': v.characterDescription?.trim() || '可愛的 VTuber',
        '{nickname}': nickname,
        '{alarm_time}': v.alarmTime,
        '{time_slot}': slot[v.timeSlot],
        '{weekday_zh}': WEEKDAY_ZH[v.weekday],
        '{is_weekday}': weekend ? '週末' : '平日',
        '{gender_pronoun}': v.genderPronoun || '你',
        '{purpose}': v.purpose,
        '{relationship_clause}': v.relationshipText && v.relationshipText.trim()
            ? `，${v.relationshipText.trim()}`
            : '',
        '{birthday_clause}': v.isUserBirthday ? `，今天也是 ${nickname} 的生日` : '',
        '{alarm_label_clause}': v.alarmLabel && v.alarmLabel.trim()
            ? `，是「${v.alarmLabel.trim()}」的鬧鐘`
            : '',
    };
    return tpl.replace(/\{[a-z_]+\}/g, (tok) => (tok in map ? map[tok] : tok));
}
/** Mood the app should show for a wake response — birthday wins, else slot. */
export function pickAlarmMood(timeSlot, isUserBirthday) {
    if (isUserBirthday)
        return 'birthday';
    return timeSlot;
}
